import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TimePicker } from "@/components/ui/time-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
// import { toast } from "sonner";  // TODO: Use when toast notifications are needed
// import { emailRegex } from "@/utils/validationUtils";  // TODO: Use when email validation is needed
import { DatePicker } from "@/components/ui/date-picker";
import { EnhancedMultiSelect } from "@/components/ui/enhanced-multiselect";
import { EnhancedSingleSelect } from "@/components/ui/enhanced-singleselect";
import { TagSelector } from "@/components/ui/tag-selector";
import { ControlledTextarea } from "@/components/ui/controlled-textarea";
import { Checkbox } from "@/components/ui/checkbox";
import type { EditableCellConfig } from "./types";
import {
  type CellValue,
  extractIds,
  handleValueChangeAction,
} from "./utils/cellValueUtils";
import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react/macro";
import {
  useState,
  useRef,
  useEffect,
  type KeyboardEvent,
  type MouseEvent,
} from "react";

interface EditableCellProps<T> {
  config: EditableCellConfig<T>;
  value: CellValue;
  originalValue: CellValue;
  onChange: (value: CellValue) => void;
  /**
   * Report an in-progress value WITHOUT touching table state.
   *
   * `onChange` feeds `editValue`, which the column memo depends on, so calling
   * it per keystroke remounts this cell and drops focus mid-entry. Editors that
   * must keep the table's commit path up to date while the user types (the time
   * cell) call this instead; the table stores it in a ref and prefers it when
   * committing.
   */
  onPendingChange?: (value: CellValue) => void;
  onSave: (rowId: string, field: keyof T, newValue: CellValue) => Promise<void>;
  onCancel: () => void;
  validationError: string | null;
  isSaving: boolean;
  editingCell: { rowId: string; column: keyof T } | null;
  /**
   * Opt-in spreadsheet keyboard navigation (S14). When provided, Enter commits +
   * advances DOWN and Tab commits + advances RIGHT — the table owns the commit +
   * move so the cell does not also fire its own save. Undefined = legacy behaviour.
   *
   * Only the `time` and default text inputs route their keydown through
   * `handleGridKey`. The select, enhanced-select, date, checkbox, textarea and
   * multi-select editors keep their own Enter/Tab handling and IGNORE this prop —
   * passing it does not make them navigable. Wire an editor into `handleGridKey`
   * before relying on grid nav for a column that uses it.
   */
  onGridNavigate?: (direction: 'down' | 'up' | 'right' | 'left') => void;
}

export function EditableCell<T>({
  config,
  value,
  originalValue,
  onChange,
  onPendingChange,
  onSave,
  onCancel,
  validationError,
  isSaving,
  editingCell,
  onGridNavigate,
}: EditableCellProps<T>) {
  const { i18n } = useLingui();

  // When grid keyboard-nav is enabled, Enter/Tab are handled by the table's
  // commit-and-move so the cell must NOT also run its own save path (that would
  // double-save). Returns true when the key was consumed.
  const handleGridKey = (
    e: KeyboardEvent<HTMLElement>,
  ): boolean => {
    if (!onGridNavigate) return false;
    if (e.key === 'Enter') {
      e.preventDefault();
      // Shift+Enter walks back up the column, mirroring a spreadsheet.
      onGridNavigate(e.shiftKey ? 'up' : 'down');
      return true;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      // Shift+Tab must go BACKWARDS. Treating it as a plain Tab leaves a keyboard user
      // with no way to revisit the previous cell and no way out of the grid.
      onGridNavigate(e.shiftKey ? 'left' : 'right');
      return true;
    }
    return false;
  };
  // Local state for textarea to prevent cursor jumping
  const [localTextareaValue, setLocalTextareaValue] = useState<string>(
    config.inputType === "textarea" ? (value as string || "") : ""
  );
  
  // Ref for textarea to control cursor position
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Latest value emitted by the TimePicker for the cell currently being edited.
  //
  // TimePicker fires onChange(newValue) and onBlur() from within the same blur
  // handler, so the `value` prop is still the pre-edit value when onBlur runs —
  // React has not re-rendered yet. Reading `value` there made every inline time
  // edit compare the ORIGINAL against itself, conclude "unchanged", and cancel;
  // the edit was silently discarded and never saved. onChange writes this ref
  // synchronously, so onBlur can read what the user actually entered.
  const pendingTimeRef = useRef<string | null>(null);

  // The cell this instance is editing, captured once at mount.
  //
  // Reading `editingCell` when a commit fires is a race: clicking another cell
  // re-points it before blur runs, so the edit landed on the wrong cell or was
  // compared against the wrong `originalValue` and dropped. BaseEditableTable
  // keys this component per cell (`edit-${rowId}-${column}`), so a fresh
  // instance mounts per edit and this ref is stable for that cell's lifetime.
  const editedCellRef = useRef(editingCell);

  // A time cell commits or cancels exactly once. Enter/Tab commit and then move
  // focus, which fires blur afterwards; the confirm button likewise runs after
  // its own blur would have. Without this, one edit writes twice and toasts
  // twice.
  const settledRef = useRef(false);

  const commitTime = (newValue: string) => {
    const cell = editedCellRef.current;
    if (!cell || settledRef.current) return;
    settledRef.current = true;
    handleValueChangeAction(
      newValue,
      originalValue,
      "time",
      (val) => onSave(cell.rowId, cell.column, val),
      onCancel
    );
  };

  const cancelTime = () => {
    if (settledRef.current) return;
    settledRef.current = true;
    onCancel();
  };

  const currentTimeValue = (): string =>
    pendingTimeRef.current ?? ((value as string) || "");

  // A rejected commit is not a settled cell. `onSave` resolves either way — the
  // table swallows a validation failure and keeps the editor open — so the only
  // signal that the write did not land is the error coming back. Without this
  // the cell stays latched shut: confirm, cancel and Escape all short-circuit on
  // `settledRef` and the user is stuck in an editor that can neither save nor
  // close.
  useEffect(() => {
    if (validationError) {
      settledRef.current = false;
    }
  }, [validationError]);


  // Set cursor to end when textarea is initially focused and sync local state
  useEffect(() => {
    if (config.inputType === "textarea") {
      // Sync localTextareaValue with external value prop changes
      setLocalTextareaValue(value as string || "");
      
      // Set cursor to end when textarea is focused
      if (textareaRef.current) {
        const textarea = textareaRef.current;
        // Set cursor to end of text
        const length = textarea.value.length;
        textarea.setSelectionRange(length, length);
        textarea.focus();
      }
    }
  }, [config.inputType, value]);
  
  if (isSaving) {
    return (
      <div className="flex items-center justify-center h-10 px-2">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  switch (config.inputType) {
    case "select":
      return (
        <Select
          defaultValue={value as string}
          onValueChange={(newValue: string) => {
            onChange(newValue);
            // Only save if the value actually changed
            // Compare as strings since select values are always strings
            if (editingCell && newValue !== String(originalValue)) {
              onSave(editingCell.rowId, editingCell.column, newValue);
            }
          }}
        >
          <SelectTrigger
            className="w-full h-10 border-primary bg-background hover:bg-muted/50 focus:ring-2 focus:ring-primary/20 px-2 rounded-none"
            autoFocus
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-popover border-border shadow-lg">
            {config.selectOptions?.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className="hover:bg-muted/70 focus:bg-muted/70 cursor-pointer"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case "enhanced-select":
      return (
        <div data-editing-cell="true" className="w-full">
          <EnhancedSingleSelect
            value={(value as string) || ""}
            onChange={(newValue: string) => {
              onChange(newValue);
              // Only save if the value actually changed
              if (editingCell && newValue !== String(originalValue || "")) {
                onSave(editingCell.rowId, editingCell.column, newValue);
              }
            }}
            options={config.enhancedSelectOptions || config.selectOptions?.map(opt => ({ ...opt })) || []}
            renderOption={config.renderEnhancedOption}
            className="border-primary"
          />
        </div>
      );

    case "multi-select": {
      // For labels, value is an array of label objects
      const currentIds = Array.isArray(value) ? extractIds(value) : [];

      // For multi-select, use the value prop which contains the current edit state.
      // `extractIds` handles both shapes (an array of ids and an array of label
      // objects), so the editor always receives `string[]`.
      const editIds = currentIds;

      return (
        <div
          className="w-full"
          onClick={(e) => e.stopPropagation()}
          data-editing-cell="true"
        >
          <EnhancedMultiSelect
            value={editIds}
            onChange={(newIds: string[]) => {
              onChange(newIds);
              // Don't auto-save for multi-select, wait for explicit save
            }}
            options={
              (config.multiSelectOptions || []).map((opt) => ({
                ...opt,
                color: opt.color || undefined, // Convert null to undefined
              })) as Array<{ value: string; label: string; color?: string }>
            }
            placeholder={i18n._({
              id: "table.cell.multiSelect.placeholder",
              message: "Select items...",
            })}
            className="min-h-10"
          />
          <div className="flex gap-2 mt-2">
            <Button
              type="button"
              size="sm"
              onClick={() => {
                if (editingCell) {
                  onSave(editingCell.rowId, editingCell.column, editIds);
                }
              }}
            >
              {i18n._({ id: "common.save", message: "Save" })}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={onCancel}>
              {i18n._({ id: "common.cancel", message: "Cancel" })}
            </Button>
          </div>
        </div>
      );
    }

    case "tag-select": {
      // For labels, value is an array of label objects - extract IDs like in GroupForm
      // Use originalValue for initial state to ensure proper pre-selection
      const currentIds = Array.isArray(originalValue)
        ? extractIds(originalValue)
        : [];

      return (
        <div
          className="w-full"
          onClick={(e) => e.stopPropagation()}
          data-editing-cell="true"
        >
          <TagSelector
            value={currentIds}
            onChange={(newIds: string[]) => {
              // Update the edit state first
              onChange(newIds);
              // Auto-save on change like other table fields
              if (editingCell) {
                onSave(editingCell.rowId, editingCell.column, newIds);
              }
            }}
            options={(config.multiSelectOptions || []).map((opt) => ({
              value: opt.value,
              label: opt.label,
              color: opt.color || undefined, // Convert null to undefined
            }))}
            className="min-h-10"
            autoFocus
          />
        </div>
      );
    }

    case "textarea": {
      return (
        <div
          className="relative w-full"
          data-editing-cell="true"
        >
          <ControlledTextarea
            ref={textareaRef}
            value={localTextareaValue}
            onChange={(newValue) => {
              // Update local state only, not parent state
              setLocalTextareaValue(newValue as string);
            }}
            onBlur={(e) => {
              const currentValue = e.target.value;
              if (editingCell) {
                // Update parent state right before saving
                onChange(currentValue);
                handleValueChangeAction(
                  currentValue,
                  originalValue,
                  config.inputType || "text",
                  (value) =>
                    onSave(editingCell.rowId, editingCell.column, value),
                  onCancel
                );
              }
            }}
            onKeyDown={(e) => {
              // Check for Ctrl+Enter (Windows/Linux) or Cmd+Enter (Mac) to save
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault(); // Prevent default newline insertion
                const currentValue = (e.target as HTMLTextAreaElement).value;
                if (editingCell) {
                  // Update parent state right before saving
                  onChange(currentValue);
                  handleValueChangeAction(
                    currentValue,
                    originalValue,
                    config.inputType || "text",
                    (value) =>
                      onSave(editingCell.rowId, editingCell.column, value),
                    onCancel
                  );
                }
              } else if (e.key === "Escape") {
                onCancel();
              }
              // Regular Enter key adds a new line (default behavior)
            }}
            className={cn(
              "min-h-[60px] border-primary bg-background hover:bg-muted/30 focus:ring-2 focus:ring-primary/20",
              "rounded-none resize-vertical text-left",
              validationError
                ? "border-destructive focus:ring-destructive/20"
                : ""
            )}
            style={{
              width: "100%",
              minWidth: "100%",
              paddingLeft: "8px",
              paddingRight: "8px",
            }}
            autoFocus
          />
          {validationError && (
            <div className="absolute text-xs text-destructive mt-1 bg-background/90 px-1 rounded z-10 top-full">
              {validationError}
            </div>
          )}
          <div className="text-xs text-muted-foreground mt-1">
            <Trans id="table.textarea.saveHint">Press Ctrl+Enter to save • Esc to cancel</Trans>
          </div>
        </div>
      );
    }

    case "checkbox":
      return (
        <div
          className="flex items-center justify-center h-10"
          data-editing-cell="true"
        >
          <Checkbox
            checked={value as boolean}
            onCheckedChange={(checked) => {
              const newValue = !!checked;
              onChange(newValue);
              // Auto-save on change like select fields
              if (editingCell && newValue !== originalValue) {
                onSave(editingCell.rowId, editingCell.column, newValue);
              }
            }}
            className="h-5 w-5"
          />
        </div>
      );

    case "date": {
      // Ensure we have a proper Date object for the picker
      const dateValue = (() => {
        if (!value) return null;
        if (value instanceof Date) return value;
        if (typeof value === "string") {
          try {
            return new Date(value);
          } catch {
            return null;
          }
        }
        return null;
      })();

      return (
        <div className="relative" data-editing-cell="true">
          <DatePicker
            value={dateValue || undefined}
            onChange={(date) => {
              onChange(date);
              if (editingCell) {
                // Compare the date values - handle null/undefined cases
                const normalizedNewValue = date ?? null;

                // Convert originalValue to Date for comparison
                let normalizedOriginalValue: Date | null = null;
                if (originalValue instanceof Date) {
                  normalizedOriginalValue = originalValue;
                } else if (typeof originalValue === "string" && originalValue) {
                  normalizedOriginalValue = new Date(originalValue);
                }

                // Only save if the value actually changed
                if (
                  normalizedNewValue?.getTime() !==
                  normalizedOriginalValue?.getTime()
                ) {
                  onSave(
                    editingCell.rowId,
                    editingCell.column,
                    normalizedNewValue
                  );
                }
              }
            }}
            className="h-10 border-primary bg-background hover:bg-muted/50 focus:ring-2 focus:ring-primary/20 font-mono text-sm rounded-none [&_svg]:hidden [&_button]:pl-0 [&_button]:pr-2 [&_button]:ml-0 [&_button]:!pl-0 [&_button]:rounded-none"
          />
          {validationError && (
            <div className="absolute text-xs text-destructive mt-1 bg-background/90 px-1 rounded z-10 top-full">
              {validationError}
            </div>
          )}
        </div>
      );
    }

    case "time": {
      // Keeping focus inside the cell is what makes the buttons work: without
      // it, mousedown blurs the input and the blur commit runs before the click
      // ever lands, so cancel could never win and confirm would fire twice.
      const keepFocus = (e: MouseEvent) => e.preventDefault();

      return (
        <div
          className="relative flex items-center w-full gap-1"
          data-editing-cell="true"
          onKeyDown={(e) => {
            if (handleGridKey(e)) {
              // The table owns the commit-and-move; mark this cell settled so
              // the blur that follows the focus move does not write again.
              settledRef.current = true;
              return;
            }
            // Without grid navigation nothing consumes Enter, so the key that
            // every other editor commits with used to do nothing here and the
            // edit only landed once the cell happened to blur. Buttons keep
            // their own Enter (it fires their click), so skip those.
            if (e.key === "Enter" && !(e.target instanceof HTMLButtonElement)) {
              e.preventDefault();
              commitTime(currentTimeValue());
              return;
            }
            if (e.key === "Escape") {
              cancelTime();
            }
          }}
        >
          <TimePicker
            value={(value as string) || ""}
            commitMode="live"
            onChange={(newTime) => {
              // Refs only — NEVER `onChange`, for as long as the editor is open.
              //
              // `onChange` feeds `editValue`, a dependency of the column memo,
              // so any call rebuilds the column defs and remounts these inputs.
              // That kills the interaction mid-edit: on a keystroke it drops the
              // caret, and on a hour→minute move it replaces the very input the
              // user is tabbing into. Every commit path here reads these refs,
              // so table state only needs to change once, at commit.
              pendingTimeRef.current = newTime;
              onPendingChange?.(newTime);
            }}
            onBlur={() => commitTime(currentTimeValue())}
            className={cn(
              validationError
                ? "border-destructive focus:ring-destructive/20"
                : ""
            )}
          />
          <button
            type="button"
            onMouseDown={keepFocus}
            // Deliberately NOT disabled while an error is showing: the message
            // survives until the next successful save, so disabling it left the
            // user unable to confirm the value they had just corrected. The
            // table re-validates on every commit anyway.
            onClick={() => commitTime(currentTimeValue())}
            aria-label={i18n._({
              id: "table.cell.time.confirm",
              message: "Confirm time",
            })}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Check className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onMouseDown={keepFocus}
            onClick={cancelTime}
            aria-label={i18n._({
              id: "table.cell.time.cancel",
              message: "Cancel time edit",
            })}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
          {validationError && (
            <div className="absolute text-xs text-destructive mt-1 bg-background/90 px-1 rounded z-10 top-full">
              {validationError}
            </div>
          )}
        </div>
      );
    }

    default:
      return (
        <div
          className="relative flex items-center w-full"
          data-editing-cell="true"
        >
          <Input
            type={config.inputType || "text"}
            defaultValue={value as string}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            onBlur={(e) => {
              const currentInputValue = e.target.value;
              if (editingCell) {
                handleValueChangeAction(
                  currentInputValue,
                  originalValue,
                  config.inputType || "text",
                  (value) =>
                    onSave(editingCell.rowId, editingCell.column, value),
                  onCancel
                );
              }
            }}
            onKeyDown={(e) => {
              if (handleGridKey(e)) return;
              if (e.key === "Enter" && editingCell) {
                const currentInputValue = (e.target as HTMLInputElement).value;
                handleValueChangeAction(
                  currentInputValue,
                  originalValue,
                  config.inputType || "text",
                  (value) =>
                    onSave(editingCell.rowId, editingCell.column, value),
                  onCancel
                );
              } else if (e.key === "Escape") {
                onCancel();
              }
            }}
            className={cn(
              "h-10 border-primary bg-background hover:bg-muted/30 focus:ring-2 focus:ring-primary/20",
              // Remove rounded corners for table cells
              "rounded-none",
              // Right-align number inputs, left-align others
              config.inputType === "number" ? "text-right" : "text-left",
              // Add monospace font based on configuration
              config.useMonospaceFont ? "font-mono text-sm" : "",
              validationError
                ? "border-destructive focus:ring-destructive/20"
                : ""
            )}
            style={{
              // Use full available width with minimal padding
              width: "100%",
              minWidth: "100%",
              paddingLeft: "8px", // Match display cell px-2 padding
              paddingRight: "8px",
            }}
            autoFocus
          />
          {validationError && (
            <div className="absolute text-xs text-destructive mt-1 bg-background/90 px-1 rounded z-10 top-full">
              {validationError}
            </div>
          )}
        </div>
      );
  }
}
